import { createApi } from "@reduxjs/toolkit/query/react";

import { triggerFileDownload } from "@/shared/helpers/trigger-file-download.helper";
import { type IApiResponse } from "@/shared/interfaces/api-response.interface";
import type {
  IBoundaryGeometryResponse,
  IBoundaryGeometriesPageResponse,
} from "@/shared/interfaces/boundary-geometry.interface";
import type { IEnqueueDistrictImportResponse } from "@/shared/interfaces/district-import.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

import { fetchMyDistrictSchoolsCsv, fetchSchoolBoundariesZip } from "./functions";
import {
  type ICreateUploadUrlRequest,
  type IGetMyDistrictSchoolsParams,
  type IDownloadSchoolBoundariesZipSuccessResult,
  type IExportSchoolBoundariesZipArg,
  type IExportMyDistrictSchoolsArg,
  type IDownloadMyDistrictSchoolsSuccessResult,
  type IAddAttendanceBoundaryArchiveRequest,
  type IImportAttendanceBoundariesRequest,
  type IImportAttendanceBoundariesResponse,
  type IRemoveAttendanceBoundaryArchiveRequest,
  type IImportFileExtensionsResponse,
  type IImportSchoolCapacityRequest,
  type IImportSchoolListRequest,
  type IImportSchoolListResponse,
  type IMyDistrictSchoolsResponse,
  type IFinalizeSchoolDataImportRequest,
  type IFinalizeAttendanceBoundariesBatchRequest,
  type IFinalizeAttendanceBoundariesBatchResponse,
  type ISchoolBoundaryPreviewListResponse,
  type IGetAttendanceBoundaryPreviewsParams,
  type IGetAttendanceBoundaryPreviewGeometriesParams,
  type IGetUnassignedAttendanceBoundaryPreviewsParams,
  type ISchoolBoundaryPreviewUnassignedListResponse,
  type IGetUnassignedAttendanceBoundaryPreviewCountParams,
  type ISchoolBoundaryPreviewUnassignedCountResponse,
  type IGetAttendanceBoundaryPreviewDetailsParams,
  type ISchoolBoundaryPreviewDetails,
  type IUpsertSchoolBoundaryPreviewsRequest,
  type IResolveGapFillResponse,
  type IResolveAutoFitResponse,
  type IResolveSchoolBoundaryPreviewGapFillRequest,
  type IResolveSchoolBoundaryPreviewAutoFitRequest,
  type ISchoolLevelResponse,
  type ISchoolWithDetailsResponse,
  type ISchoolForEditResponse,
  type ISchoolHasStudentsResponse,
  type ISchoolInternalIdAvailabilityRequest,
  type ISchoolInternalIdAvailabilityResponse,
  type ISchoolProgramResponse,
  type IUpsertSchoolProgramsRequest,
  type IUpsertSchoolProgramsResponse,
  type ISchoolDatesOfOperationResponse,
  type ISchoolDatesOfOperationPeriodResponse,
  type IUpsertSchoolDatesOfOperationRequest,
  type ISchoolCapacityByYearResponse,
  type IUpsertSchoolCapacityRequest,
  type ICreateSchoolRequest,
  type IUpdateSchoolRequest,
  type IUploadUrlResponse,
  type ImportType,
} from "./schools.interface";

/**
 * District levels only list levels that have schools, so adding, relinking or removing schools can change them.
 * districtsApi is a separate slice (and imports this one), so it is loaded lazily.
 */
async function invalidateDistrictLevelsOnSuccess(
  dispatch: (action: unknown) => unknown,
  queryFulfilled: Promise<unknown>,
): Promise<void> {
  try {
    await queryFulfilled;
    const { default: districtsApi, DISTRICT_LEVELS_TAG } = await import("../districts/districts");
    dispatch(districtsApi.util.invalidateTags([DISTRICT_LEVELS_TAG]));
  } catch {
    // leave failed; no cache invalidation
  }
}

const schoolsApi = createApi({
  reducerPath: "schoolsApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Schools, ApiTags.Documents, ApiTags.BoundaryGeometry],
  endpoints: (builder) => ({
    getImportFileExtensions: builder.query<IImportFileExtensionsResponse, ImportType>({
      query: (importType) => ({
        url: "/schools/import/file-extensions",
        method: "GET",
        params: { importType },
      }),
      providesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<IImportFileExtensionsResponse>) => result.data,
    }),
    createUploadUrl: builder.mutation<IUploadUrlResponse, ICreateUploadUrlRequest>({
      query: (body) => ({
        url: "/schools/import/upload-url",
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IUploadUrlResponse>) => result.data,
    }),
    importSchoolList: builder.mutation<IImportSchoolListResponse, IImportSchoolListRequest>({
      query: (body) => ({
        url: "/schools/import/list",
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IImportSchoolListResponse>) => result.data,
    }),
    importSchoolCapacity: builder.mutation<IEnqueueDistrictImportResponse, IImportSchoolCapacityRequest>({
      query: (body) => ({
        url: "/schools/import/capacity",
        method: "POST",
        body,
      }),
      transformResponse: (
        result:
          | IApiResponse<IEnqueueDistrictImportResponse>
          | { statusCode: number; data: IEnqueueDistrictImportResponse; [key: string]: unknown },
      ) => {
        if ("data" in result && result.data) {
          return result.data;
        }
        return result as unknown as IEnqueueDistrictImportResponse;
      },
    }),
    getSchoolLevels: builder.query<ISchoolLevelResponse[], string>({
      query: (districtId) => ({
        url: `/districts/${districtId}/levels`,
        method: "GET",
      }),
      providesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<ISchoolLevelResponse[]>) => result.data,
    }),
    importAttendanceBoundaries: builder.mutation<IEnqueueDistrictImportResponse, IImportAttendanceBoundariesRequest>({
      query: (body) => ({
        url: "/schools/import/boundaries",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Documents], // Invalidate documents cache to refresh the list
      transformResponse: (
        result:
          | IApiResponse<IEnqueueDistrictImportResponse>
          | { statusCode: number; data: IEnqueueDistrictImportResponse; [key: string]: unknown },
      ) => {
        if ("data" in result && result.data) {
          return result.data;
        }
        return result as unknown as IEnqueueDistrictImportResponse;
      },
    }),
    addAttendanceBoundaryArchive: builder.mutation<
      IImportAttendanceBoundariesResponse,
      IAddAttendanceBoundaryArchiveRequest
    >({
      query: (body) => ({
        url: "/schools/import/boundaries/session/add-archive",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Documents],
      transformResponse: (result: IApiResponse<IImportAttendanceBoundariesResponse>) => result.data,
    }),
    removeAttendanceBoundaryArchive: builder.mutation<
      IImportAttendanceBoundariesResponse,
      IRemoveAttendanceBoundaryArchiveRequest
    >({
      query: (body) => ({
        url: "/schools/import/boundaries/session/remove-archive",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Documents],
      transformResponse: (result: IApiResponse<IImportAttendanceBoundariesResponse>) => result.data,
    }),
    finalizeAttendanceBoundaries: builder.mutation<
      IFinalizeAttendanceBoundariesBatchResponse,
      IFinalizeAttendanceBoundariesBatchRequest
    >({
      query: (body) => ({
        url: "/schools/import/boundaries/finalize",
        method: "POST",
        body,
      }),
      // Do not use declarative invalidatesTags here: RTK runs calculateProvidedByThunk
      // which looks up endpointDefinitions[action.meta.arg.endpointName].invalidatesTags;
      // with parallel finalize calls this can throw (undefined endpointDefinition).
      // Invalidate after success instead (same pattern as util.invalidateTags).
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            schoolsApi.util.invalidateTags([
              { type: ApiTags.Schools },
              { type: ApiTags.Documents },
            ]),
          );
          const { default: districtsApi } = await import("../districts/districts");
          dispatch(districtsApi.util.invalidateTags([ApiTags.Districts]));
        } catch {
          // leave failed; no cache invalidation
        }
      },
      transformResponse: (
        result:
          | IApiResponse<IFinalizeAttendanceBoundariesBatchResponse>
          | { statusCode: number; data: IFinalizeAttendanceBoundariesBatchResponse; [key: string]: unknown },
      ) =>
        "data" in result && result.data
          ? result.data
          : (result as unknown as IFinalizeAttendanceBoundariesBatchResponse),
    }),
    getAttendanceBoundaryPreviews: builder.query<
      ISchoolBoundaryPreviewListResponse,
      IGetAttendanceBoundaryPreviewsParams
    >({
      query: ({ districtId, documentIds, schoolLevelType }) => {
        const params = new URLSearchParams({ districtId });
        documentIds.forEach((id) => params.append("documentIds", id));
        if (schoolLevelType != null) {
          params.set("schoolLevelType", schoolLevelType);
        }

        return {
          url: "/schools/import/boundaries/previews",
          method: "GET",
          params,
        };
      },
      providesTags: (_result, _error, { documentIds }) =>
        documentIds.map((documentId) => ({
          type: ApiTags.Documents as const,
          id: `attendance-boundary-previews-${documentId}`,
        })),
      transformResponse: (result: IApiResponse<ISchoolBoundaryPreviewListResponse>) => result.data,
    }),
    getUnassignedAttendanceBoundaryPreviews: builder.query<
      ISchoolBoundaryPreviewUnassignedListResponse,
      IGetUnassignedAttendanceBoundaryPreviewsParams
    >({
      query: ({ districtId, documentIds }) => {
        const params = new URLSearchParams({ districtId });
        documentIds.forEach((id) => params.append("documentIds", id));

        return {
          url: "/schools/import/boundaries/previews/unassigned",
          method: "GET",
          params,
        };
      },
      providesTags: (_result, _error, { documentIds }) =>
        documentIds.map((documentId) => ({
          type: ApiTags.Documents as const,
          id: `attendance-boundary-previews-${documentId}`,
        })),
      transformResponse: (result: IApiResponse<ISchoolBoundaryPreviewUnassignedListResponse>) =>
        result.data,
    }),
    getUnassignedAttendanceBoundaryPreviewCount: builder.query<
      ISchoolBoundaryPreviewUnassignedCountResponse,
      IGetUnassignedAttendanceBoundaryPreviewCountParams
    >({
      query: ({ districtId, documentIds }) => {
        const params = new URLSearchParams({ districtId });
        documentIds.forEach((id) => params.append("documentIds", id));

        return {
          url: "/schools/import/boundaries/previews/unassigned/count",
          method: "GET",
          params,
        };
      },
      transformResponse: (result: IApiResponse<ISchoolBoundaryPreviewUnassignedCountResponse>) =>
        result.data,
    }),
    getAttendanceBoundaryPreviewDetails: builder.query<
      ISchoolBoundaryPreviewDetails,
      IGetAttendanceBoundaryPreviewDetailsParams
    >({
      query: ({ previewId }) => ({
        url: `/schools/import/boundaries/previews/${previewId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { previewId }) => [
        { type: ApiTags.Documents, id: `attendance-boundary-preview-${previewId}` },
      ],
      transformResponse: (result: IApiResponse<ISchoolBoundaryPreviewDetails>) => result.data,
    }),
    getAttendanceBoundaryPreviewGeometry: builder.query<
      IBoundaryGeometryResponse,
      IGetAttendanceBoundaryPreviewDetailsParams
    >({
      query: ({ previewId }) => ({
        url: `/schools/import/boundaries/previews/${previewId}/geometry`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, { previewId }) => [
        { type: ApiTags.BoundaryGeometry, id: `attendance:${previewId}` },
        { type: ApiTags.BoundaryGeometry, id: "attendance:LIST" },
      ],
      transformResponse: (result: IApiResponse<IBoundaryGeometryResponse>) => result.data,
    }),
    getAttendanceBoundaryPreviewGeometries: builder.query<
      IBoundaryGeometriesPageResponse,
      IGetAttendanceBoundaryPreviewGeometriesParams
    >({
      query: ({ districtId, documentIds, schoolLevelType, cursor, limit }) => {
        const params = new URLSearchParams({ districtId, schoolLevelType });
        documentIds.forEach((id) => params.append("documentIds", id));
        if (cursor != null) {
          params.set("cursor", cursor);
        }
        if (limit != null) {
          params.set("limit", String(limit));
        }

        return {
          url: "/schools/import/boundaries/previews/geometries",
          method: "GET",
          params,
        };
      },
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, { documentIds, schoolLevelType }) => [
        { type: ApiTags.BoundaryGeometry, id: "attendance:LIST" },
        { type: ApiTags.BoundaryGeometry, id: `attendance-page:${schoolLevelType}` },
        ...documentIds.map((documentId) => ({
          type: ApiTags.BoundaryGeometry as const,
          id: `attendance-doc:${documentId}`,
        })),
      ],
      transformResponse: (result: IApiResponse<IBoundaryGeometriesPageResponse>) => result.data,
    }),
    upsertAttendanceBoundaryPreviews: builder.mutation<
      ISchoolBoundaryPreviewListResponse,
      IUpsertSchoolBoundaryPreviewsRequest
    >({
      query: ({ districtId, boundaries }) => ({
        url: `/schools/${districtId}/import/boundaries/previews/bulk`,
        method: "PUT",
        body: { boundaries },
      }),
      invalidatesTags: (result, _error, { boundaries }) => {
        const documentIds = [...new Set(boundaries.map((boundary) => boundary.documentId))];
        const previewIds = [
          ...new Set(
            [
              ...boundaries.flatMap((boundary) => (boundary.id ? [boundary.id] : [])),
              ...(result?.items?.map((item) => item.id) ?? []),
            ],
          ),
        ];

        return [
          ...documentIds.map((documentId) => ({
            type: ApiTags.Documents as const,
            id: `attendance-boundary-previews-${documentId}`,
          })),
          ...(result?.items?.map((item) => ({
            type: ApiTags.Documents as const,
            id: `attendance-boundary-preview-${item.id}`,
          })) ?? []),
          { type: ApiTags.BoundaryGeometry as const, id: "attendance:LIST" },
          ...previewIds.map((previewId) => ({
            type: ApiTags.BoundaryGeometry as const,
            id: `attendance:${previewId}`,
          })),
        ];
      },
      transformResponse: (result: IApiResponse<ISchoolBoundaryPreviewListResponse>) => result.data,
    }),
    resolveAttendanceBoundaryPreviewGapFill: builder.mutation<
      IResolveGapFillResponse,
      {
        districtId: string;
        body: IResolveSchoolBoundaryPreviewGapFillRequest;
      }
    >({
      query: ({ districtId, body }) => ({
        url: `/schools/${districtId}/import/boundaries/previews/gap-fill/resolve`,
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IResolveGapFillResponse>) => result.data,
    }),
    resolveAttendanceBoundaryPreviewAutoFit: builder.mutation<
      IResolveAutoFitResponse,
      {
        districtId: string;
        body: IResolveSchoolBoundaryPreviewAutoFitRequest;
      }
    >({
      query: ({ districtId, body }) => ({
        url: `/schools/${districtId}/import/boundaries/previews/auto-fit/resolve`,
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IResolveAutoFitResponse>) => result.data,
    }),
    getSchoolForEdit: builder.query<ISchoolForEditResponse, string>({
      query: (schoolId) => ({
        url: `/schools/${schoolId}/edit`,
        method: "GET",
      }),
      providesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<ISchoolForEditResponse>) => result.data,
    }),
    getSchoolHasStudents: builder.query<ISchoolHasStudentsResponse, string>({
      query: (schoolId) => ({
        url: `/schools/${schoolId}/has-students`,
        method: "GET",
      }),
      transformResponse: (result: IApiResponse<ISchoolHasStudentsResponse>) => result.data,
    }),
    getSchoolInternalIdAvailability: builder.query<
      ISchoolInternalIdAvailabilityResponse,
      ISchoolInternalIdAvailabilityRequest
    >({
      query: ({ districtId, internalId, excludeSchoolId }) => ({
        url: "/schools/internal-id/availability",
        method: "GET",
        params: {
          districtId,
          internalId,
          ...(excludeSchoolId ? { excludeSchoolId } : {}),
        },
      }),
      transformResponse: (result: IApiResponse<ISchoolInternalIdAvailabilityResponse>) => result.data,
    }),
    updateSchool: builder.mutation<ISchoolForEditResponse, { schoolId: string; body: IUpdateSchoolRequest }>({
      query: ({ schoolId, body }) => ({
        url: `/schools/${schoolId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [ApiTags.Schools],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await invalidateDistrictLevelsOnSuccess(dispatch, queryFulfilled);
      },
      transformResponse: (result: IApiResponse<ISchoolForEditResponse>) => result.data,
    }),
    deleteSchool: builder.mutation<void, string>({
      query: (schoolId) => ({
        url: `/schools/${schoolId}`,
        method: "DELETE",
      }),
      invalidatesTags: [ApiTags.Schools],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await invalidateDistrictLevelsOnSuccess(dispatch, queryFulfilled);
      },
    }),
    createSchool: builder.mutation<ISchoolForEditResponse, ICreateSchoolRequest>({
      query: (body) => ({
        url: "/schools",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Schools],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await invalidateDistrictLevelsOnSuccess(dispatch, queryFulfilled);
      },
      transformResponse: (result: IApiResponse<ISchoolForEditResponse>) => result.data,
    }),
    getSchoolWithDetails: builder.query<ISchoolWithDetailsResponse, string>({
      query: (schoolId) => ({
        url: `/schools/${schoolId}`,
        method: "GET",
      }),
      providesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<ISchoolWithDetailsResponse>) => result.data,
    }),
    getSchoolDatesOfOperation: builder.query<ISchoolDatesOfOperationResponse, string>({
      query: (schoolId) => ({
        url: `/schools/${schoolId}/dates-of-operation`,
        method: "GET",
      }),
      providesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<ISchoolDatesOfOperationResponse>) => result.data ?? { items: [] },
    }),
    getSchoolCapacity: builder.query<ISchoolCapacityByYearResponse, string>({
      query: (schoolId) => ({
        url: `/schools/${schoolId}/capacity`,
        method: "GET",
      }),
      providesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<ISchoolCapacityByYearResponse>) => result.data ?? { capacityByYear: {} },
    }),
    getSchoolPrograms: builder.query<ISchoolProgramResponse[], string>({
      query: (schoolId) => ({
        url: `/schools/${schoolId}/programs`,
        method: "GET",
      }),
      providesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<ISchoolProgramResponse[]>) => result.data ?? [],
    }),
    getSchoolProgramTypes: builder.query<string[], string>({
      query: (districtId) => ({
        url: "/schools/program-types",
        method: "GET",
        params: { districtId },
      }),
      providesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<string[]>) => result.data ?? [],
    }),
    getSchoolProgramDualImmersionLanguages: builder.query<string[], string>({
      query: (schoolId) => ({
        url: `/schools/${schoolId}/programs/dual-immersion-languages`,
        method: "GET",
      }),
      providesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<string[]>) => result.data ?? [],
    }),
    upsertSchoolPrograms: builder.mutation<
      IUpsertSchoolProgramsResponse,
      { schoolId: string; body: IUpsertSchoolProgramsRequest }
    >({
      query: ({ schoolId, body }) => ({
        url: `/schools/${schoolId}/programs`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<IUpsertSchoolProgramsResponse>) => result.data,
    }),
    deleteSchoolProgram: builder.mutation<void, { schoolId: string; programId: string }>({
      query: ({ schoolId, programId }) => ({
        url: `/schools/${schoolId}/programs/${programId}`,
        method: "DELETE",
      }),
      invalidatesTags: [ApiTags.Schools],
    }),
    createSchoolDatesOfOperation: builder.mutation<
      ISchoolDatesOfOperationPeriodResponse,
      { schoolId: string; body: IUpsertSchoolDatesOfOperationRequest }
    >({
      query: ({ schoolId, body }) => ({
        url: `/schools/${schoolId}/dates-of-operation`,
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<ISchoolDatesOfOperationPeriodResponse>) => result.data,
    }),
    updateSchoolDatesOfOperation: builder.mutation<
      ISchoolDatesOfOperationPeriodResponse,
      { schoolId: string; periodId: string; body: IUpsertSchoolDatesOfOperationRequest }
    >({
      query: ({ schoolId, periodId, body }) => ({
        url: `/schools/${schoolId}/dates-of-operation/${periodId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<ISchoolDatesOfOperationPeriodResponse>) => result.data,
    }),
    deleteSchoolDatesOfOperation: builder.mutation<void, { schoolId: string; periodId: string }>({
      query: ({ schoolId, periodId }) => ({
        url: `/schools/${schoolId}/dates-of-operation/${periodId}`,
        method: "DELETE",
      }),
      invalidatesTags: [ApiTags.Schools],
    }),
    upsertSchoolCapacity: builder.mutation<
      ISchoolCapacityByYearResponse,
      { schoolId: string; body: IUpsertSchoolCapacityRequest }
    >({
      query: ({ schoolId, body }) => ({
        url: `/schools/${schoolId}/capacity`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<ISchoolCapacityByYearResponse>) => result.data,
    }),
    deleteSchoolCapacity: builder.mutation<void, { schoolId: string; year: number }>({
      query: ({ schoolId, year }) => ({
        url: `/schools/${schoolId}/capacity/${year}`,
        method: "DELETE",
      }),
      invalidatesTags: [ApiTags.Schools],
    }),
    getMyDistrictSchools: builder.query<IMyDistrictSchoolsResponse, IGetMyDistrictSchoolsParams>({
      query: ({ districtId, search, schoolLevels, educationalProgramTypes, statuses }) => ({
        url: "/schools/list",
        method: "GET",
        params: {
          districtId,
          ...(search?.trim() ? { search: search.trim() } : {}),
          ...(schoolLevels?.length ? { schoolLevels } : {}),
          ...(educationalProgramTypes?.length ? { educationalProgramTypes } : {}),
          ...(statuses?.length ? { statuses } : {}),
        },
      }),
      providesTags: [ApiTags.Schools],
      transformResponse: (result: IApiResponse<IMyDistrictSchoolsResponse>) => {
        const data = result.data ?? { items: [] };
        return {
          items: data.items.map((item) => ({
            ...item,
            internalId: item.internalId ?? "",
            schoolLevelIds: item.schoolLevelIds ?? [],
            hasStudents: item.hasStudents ?? false,
          })),
        };
      },
    }),
    finalizeSchoolDataImport: builder.mutation<
      IEnqueueDistrictImportResponse,
      { schoolDataImportId: string; body: IFinalizeSchoolDataImportRequest }
    >({
      query: ({ schoolDataImportId, body }) => ({
        url: `/schools/import/${schoolDataImportId}/finalize`,
        method: "POST",
        body,
      }),
      transformResponse: (
        result:
          | IApiResponse<IEnqueueDistrictImportResponse>
          | { statusCode: number; data: IEnqueueDistrictImportResponse; [key: string]: unknown },
      ) => ("data" in result && result.data ? result.data : (result as unknown as IEnqueueDistrictImportResponse)),
    }),
    deleteSchoolDataImport: builder.mutation<void, string>({
      query: (schoolDataImportId) => ({
        url: `/schools/import/${schoolDataImportId}`,
        method: "DELETE",
      }),
      invalidatesTags: [ApiTags.Schools, ApiTags.Documents],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await invalidateDistrictLevelsOnSuccess(dispatch, queryFulfilled);
      },
    }),
    downloadSchoolBoundariesZip: builder.mutation<
      IDownloadSchoolBoundariesZipSuccessResult,
      IExportSchoolBoundariesZipArg
    >({
      queryFn: async (arg) => {
        const result = await fetchSchoolBoundariesZip(arg);

        if (result.error) {
          return { error: { status: result.error.statusCode, data: result.error } };
        }

        triggerFileDownload(result.data!.blob, result.data!.filename);
        return {
          data: {
            success: true as const,
            filename: result.data!.filename,
          },
        };
      },
    }),
    downloadMyDistrictSchools: builder.mutation<
      IDownloadMyDistrictSchoolsSuccessResult,
      IExportMyDistrictSchoolsArg
    >({
      queryFn: async (arg) => {
        const result = await fetchMyDistrictSchoolsCsv(arg);

        if (result.error) {
          return { error: { status: result.error.statusCode, data: result.error } };
        }

        triggerFileDownload(result.data!.blob, result.data!.filename);
        return {
          data: {
            success: true as const,
            filename: result.data!.filename,
          },
        };
      },
    }),
  }),
});

export const {
  useGetImportFileExtensionsQuery,
  useCreateUploadUrlMutation,
  useImportSchoolListMutation,
  useImportSchoolCapacityMutation,
  useGetSchoolLevelsQuery,
  useImportAttendanceBoundariesMutation,
  useAddAttendanceBoundaryArchiveMutation,
  useRemoveAttendanceBoundaryArchiveMutation,
  useFinalizeAttendanceBoundariesMutation,
  useGetAttendanceBoundaryPreviewsQuery,
  useLazyGetAttendanceBoundaryPreviewsQuery,
  useGetUnassignedAttendanceBoundaryPreviewsQuery,
  useLazyGetUnassignedAttendanceBoundaryPreviewsQuery,
  useLazyGetUnassignedAttendanceBoundaryPreviewCountQuery,
  useGetAttendanceBoundaryPreviewDetailsQuery,
  useLazyGetAttendanceBoundaryPreviewDetailsQuery,
  useGetAttendanceBoundaryPreviewGeometryQuery,
  useLazyGetAttendanceBoundaryPreviewGeometryQuery,
  useLazyGetAttendanceBoundaryPreviewGeometriesQuery,
  useGetAttendanceBoundaryPreviewGeometriesQuery,
  useUpsertAttendanceBoundaryPreviewsMutation,
  useResolveAttendanceBoundaryPreviewGapFillMutation,
  useResolveAttendanceBoundaryPreviewAutoFitMutation,
  useGetSchoolWithDetailsQuery,
  useGetSchoolDatesOfOperationQuery,
  useGetSchoolCapacityQuery,
  useGetSchoolProgramsQuery,
  useGetSchoolProgramTypesQuery,
  useGetSchoolProgramDualImmersionLanguagesQuery,
  useUpsertSchoolProgramsMutation,
  useDeleteSchoolProgramMutation,
  useGetSchoolForEditQuery,
  useLazyGetSchoolForEditQuery,
  useGetSchoolHasStudentsQuery,
  useLazyGetSchoolHasStudentsQuery,
  useLazyGetSchoolInternalIdAvailabilityQuery,
  useUpdateSchoolMutation,
  useCreateSchoolDatesOfOperationMutation,
  useCreateSchoolMutation,
  useUpdateSchoolDatesOfOperationMutation,
  useDeleteSchoolDatesOfOperationMutation,
  useUpsertSchoolCapacityMutation,
  useDeleteSchoolCapacityMutation,
  useDeleteSchoolMutation,
  useGetMyDistrictSchoolsQuery,
  useLazyGetSchoolWithDetailsQuery,
  useFinalizeSchoolDataImportMutation,
  useDeleteSchoolDataImportMutation,
  useDownloadSchoolBoundariesZipMutation,
  useDownloadMyDistrictSchoolsMutation,
} = schoolsApi;
export default schoolsApi;
